// src/components/feedback/FeedbackWidget.tsx
//
// Floating "share feedback" widget. Collapsed by default (a small bottom-right
// button) so it never intrudes; expands to a popover on desktop and a bottom
// sheet on mobile. Submits a thumbs up/down + optional message plus page
// context to /api/feedback, which emails our monitored inbox.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { MessageSquarePlus, ThumbsUp, ThumbsDown, X, Check } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../ui/Button';
import { feedbackApi, type FeedbackContext } from '../../services/feedback-api';

const MOBILE_BREAKPOINT = 768;

// Routes that render a bottom-anchored composer / nav the FAB must clear.
const COMPOSER_ROUTES = ['/chat'];

type Rating = 'up' | 'down' | null;

/** Best-effort entity extraction: /accounts/abc-123 → { type: 'accounts', id: 'abc-123' }. */
function extractEntity(pathname: string): { entityType?: string; entityId?: string } {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length < 2) return {};
  const last = segments[segments.length - 1];
  const looksLikeId = /^[0-9a-f]{6,}$/i.test(last) || /\d/.test(last) || last.includes('-');
  if (!looksLikeId) return {};
  return { entityType: segments[segments.length - 2], entityId: last };
}

const FeedbackWidget: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState<Rating>(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT,
  );

  const containerRef = useRef<HTMLDivElement>(null);

  // Track the previous route so we can report where the user came from.
  const lastPathRef = useRef(location.pathname);
  const prevPathRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (location.pathname !== lastPathRef.current) {
      prevPathRef.current = lastPathRef.current;
      lastPathRef.current = location.pathname;
    }
  }, [location.pathname]);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const handler = (e: MediaQueryListEvent): void => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    // Reset shortly after the close animation so reopening starts fresh.
    window.setTimeout(() => {
      setRating(null);
      setMessage('');
      setSubmitted(false);
      setError(null);
    }, 200);
  }, []);

  // Desktop: close on outside click. (Mobile uses an explicit backdrop.)
  useEffect(() => {
    if (!open || isMobile) return;
    const onMouseDown = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open, isMobile, close]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  const canSubmit = rating !== null || message.trim().length > 0;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);

    const { entityType, entityId } = extractEntity(location.pathname);
    const context: FeedbackContext = {
      pageUrl: window.location.href,
      pagePath: location.pathname + location.search,
      previousPath: prevPathRef.current,
      entityType,
      entityId,
      viewport: `${window.innerWidth}×${window.innerHeight}`,
      isMobile,
      theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
      userAgent: navigator.userAgent,
      appVersion: (import.meta.env as Record<string, string | undefined>).VITE_APP_VERSION,
      clientTimestamp: new Date().toISOString(),
    };

    try {
      await feedbackApi.submit({
        rating: rating ?? undefined,
        message: message.trim() || undefined,
        context,
      });
      setSubmitted(true);
      window.setTimeout(close, 1600);
    } catch {
      setError('Could not send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, submitting, location.pathname, location.search, isMobile, rating, message, close]);

  const bottomClass = useMemo(() => {
    const onComposerRoute = COMPOSER_ROUTES.some((r) => location.pathname.startsWith(r));
    if (isMobile) return 'bottom-20'; // clear any mobile bottom nav
    if (onComposerRoute) return 'bottom-24'; // clear the chat composer
    return 'bottom-6';
  }, [isMobile, location.pathname]);

  if (!isAuthenticated) return null;

  const panel = (
    <div className="flex flex-col">
      <div className="flex items-start justify-between px-4 pt-4 pb-2">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Share your feedback</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Tell us what's working or what's not.
          </p>
        </div>
        <button
          onClick={close}
          aria-label="Close feedback"
          className="-mr-1 -mt-1 p-1 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {submitted ? (
        <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Check className="w-5 h-5" />
          </div>
          <p className="text-sm font-medium text-foreground">Thanks for the feedback!</p>
        </div>
      ) : (
        <div className="px-4 pb-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Thumbs up"
              aria-pressed={rating === 'up'}
              onClick={() => setRating((r) => (r === 'up' ? null : 'up'))}
              className={clsx(
                'flex h-9 w-9 items-center justify-center rounded-lg border transition-colors',
                rating === 'up'
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-input text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              <ThumbsUp className="w-4 h-4" />
            </button>
            <button
              type="button"
              aria-label="Thumbs down"
              aria-pressed={rating === 'down'}
              onClick={() => setRating((r) => (r === 'down' ? null : 'down'))}
              className={clsx(
                'flex h-9 w-9 items-center justify-center rounded-lg border transition-colors',
                rating === 'down'
                  ? 'border-destructive bg-destructive/10 text-destructive'
                  : 'border-input text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              <ThumbsDown className="w-4 h-4" />
            </button>
          </div>

          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={500}
            rows={4}
            placeholder="Add a comment (optional)…"
            className={clsx(
              'mt-3 block w-full resize-none rounded-lg border px-3 py-2 text-sm transition-colors',
              'text-foreground bg-card border-input',
              'placeholder:text-muted-foreground',
              'focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring',
            )}
          />

          {error && <p className="mt-2 text-xs text-destructive">{error}</p>}

          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{message.length}/500</span>
            <Button
              size="sm"
              onClick={handleSubmit}
              isLoading={submitting}
              disabled={!canSubmit}
            >
              Submit
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Trigger + desktop popover */}
      <div ref={containerRef} className={clsx('fixed right-4 sm:right-6 z-40', bottomClass)}>
        {open && !isMobile && (
          <div
            role="dialog"
            aria-label="Share feedback"
            className="absolute bottom-full right-0 mb-3 w-80 rounded-xl border border-border bg-card shadow-2xl"
          >
            {panel}
          </div>
        )}

        <button
          aria-label="Share feedback"
          aria-expanded={open}
          onClick={() => (open ? close() : setOpen(true))}
          className={clsx(
            'flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium shadow-lg transition-colors',
            'bg-primary text-primary-foreground hover:bg-primary/90',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          )}
        >
          {open && !isMobile ? (
            <X className="w-4 h-4 shrink-0" />
          ) : (
            <MessageSquarePlus className="w-4 h-4 shrink-0" />
          )}
          <span className="whitespace-nowrap">Help us get better!</span>
        </button>
      </div>

      {/* Mobile bottom sheet (separate full-screen layer) */}
      {isMobile && open && (
        <div className="fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={close} />
          <div
            role="dialog"
            aria-label="Share feedback"
            className="absolute inset-x-0 bottom-0 rounded-t-2xl border-t border-border bg-card shadow-2xl pb-[env(safe-area-inset-bottom)]"
          >
            {panel}
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
